import { loginToTenant } from '@/lib/auth'
import { queryClient } from '@/lib/queryClient'
import { disposeSignalRService } from '@/lib/signalRService'

/**
 * Moving this session to another tenant.
 *
 * <b>Why this is not just another sign-in.</b> A session acts in exactly one tenant (ADR 024 decision 1),
 * so switching means starting a new one — there is no header to change and nothing the client may assert.
 * That part is `loginToTenant`, which sends the browser to `/bff/login` with the slug as a hint. What this
 * adds is throwing away everything the *previous* tenant's session bought, and doing it first:
 *
 *  1. The SignalR connection. It was authorized by the previous session's cookie and is subscribed to the
 *     old tenant's groups. A socket that survived the switch would keep pushing the previous client's session events into
 *     a UI now showing somebody else's — live, and with nothing on screen to suggest where they came from.
 *  2. The query cache. Every entry in it was fetched as a member of the tenant being left. React Query
 *     serves cached data instantly and without asking, so the first screens after a switch would be the
 *     previous client's data rendered under the new one's name.
 *
 * <b>Why not rely on the redirect to do it.</b> Navigating away does tear the page down, and most of the
 * time that is enough. Most of the time is the problem: if the redirect is blocked, or if the user comes
 * back with the back button, the socket and the cache are still there holding another tenant's data.
 * Tearing them down explicitly costs a few milliseconds and does not depend on any of that being true.
 *
 * The order matters and is the acceptance criterion: torn down *before* the new session exists, never after.
 */
export async function switchTenant(slug: string, returnTo = '#/'): Promise<void> {
  if (!slug) {
    throw new Error('A tenant switch needs a tenant.')
  }

  // Awaited, not fired and forgotten: stop() completes the graceful close, and a connection still closing
  // while the new session starts is the race this is here to avoid.
  await disposeSignalRService()

  // clear(), not invalidateQueries(): invalidation marks data stale and keeps serving it until a refetch
  // arrives, which is exactly the window where the old tenant's rows are on screen under the new tenant.
  queryClient.clear()

  loginToTenant(slug, returnTo)
}
